import React from 'react';
import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';
import ErrorMessage from './ErrorMessage';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import styled from 'styled-components';

dayjs.extend(customParseFormat);

const DatePickerStyle = styled(DatePicker)`
  .ant-picker-input input {
    color: black !important;
  }
`;

const resolvePickerValue = (value, format) => {
  if (!value) {
    return null;
  }

  if (dayjs.isDayjs(value)) {
    return value.isValid() ? value : null;
  }

  const normalizedValue = `${value}`.trim();
  if (!normalizedValue) {
    return null;
  }

  const candidateFormats = Array.from(
    new Set([format, 'DD/MM/YYYY', 'YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ss', 'YYYY-MM-DDTHH:mm:ss.SSS[Z]'].filter(Boolean)),
  );

  for (const candidateFormat of candidateFormats) {
    const parsedValue = dayjs(normalizedValue, candidateFormat, true);
    if (parsedValue.isValid()) {
      return parsedValue;
    }
  }

  const fallbackValue = dayjs(normalizedValue);
  return fallbackValue.isValid() ? fallbackValue : null;
};

const FormDatePicker = ({
  field,
  validation,
  placeholder,
  disabled = false,
  style,
  format,
  bordered,
  allowClear,
  ...props
}) => {
  // #region use-form
  const methods = useFormContext();
  const { error } = methods.getFieldState(field, methods.formState);
  const currentValue = methods.watch(field);

  React.useEffect(() => {
    methods.register(field, validation);
  }, [methods, field, validation]);
  // #endregion

  return (
    <React.Fragment>
      <DatePickerStyle
        bordered={bordered}
        allowClear={allowClear}
        disabled={disabled}
        placeholder={placeholder}
        value={resolvePickerValue(currentValue, format)}
        onChange={(e, date) => {
          if (date) {
            methods.clearErrors(field);
            methods.setValue(field, date);
          } else {
            methods.setValue(field, '');
          }
        }}
        format={format}
        style={style}
        {...props}
      />
      {error && <ErrorMessage message={error?.message} />}
    </React.Fragment>
  );
};

FormDatePicker.propTypes = {
  field: PropTypes.string,
  validation: PropTypes.object,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  format: PropTypes.string,
  allowClear: PropTypes.bool,
  bordered: PropTypes.bool,
};

export default FormDatePicker;
